import { useEffect, useState } from 'react';
import {
  Archive, Bell, CalendarClock, CalendarDays, ChartColumn, CircleHelp, Clapperboard, ClipboardCheck, FileText, Film, Images,
  LayoutDashboard, ListVideo, LogOut, MessageCircle, Moon, PanelLeftClose, PanelLeftOpen, Search, Settings, Sun, X,
} from 'lucide-react';
import Avatar from './Avatar';
import Icon from './Icon';
import Kbd from './Kbd';
import Mark from './Mark';
import { cx } from './util';

// The four staff groups and their real pages, names in sentence case (§3.1). flag: 'ai' → shown only when
// AI Production is enabled for the deployment; internalOnly → hidden for external client users.
export const STAFF_NAV = [
  { group: 'Overview', items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }] },
  { group: 'Scripts', items: [{ to: '/scripts', label: 'Scripts', icon: FileText }, { to: '/qual', label: 'Qual', icon: ClipboardCheck }] },
  { group: 'Production', items: [
    { to: '/live', label: 'Live production', icon: Clapperboard },
    { to: '/ai', label: 'AI production', icon: Clapperboard, flag: 'ai' },
    { to: '/calendar', label: 'Production calendar', icon: CalendarDays },
    { to: '/edit-queue', label: 'Edit queue', icon: ListVideo },
    { to: '/assets', label: 'Asset library', icon: Images },
    { to: '/footage', label: 'Raw footage', icon: Film, internalOnly: true },
    { to: '/storage', label: 'Storage', icon: Archive },
  ] },
  { group: 'Management', items: [
    { to: '/scheduler', label: 'Posting scheduler', icon: CalendarClock },
    { to: '/reports', label: 'Reports', icon: ChartColumn },
    { to: '/assistant', label: 'Clio', icon: MessageCircle },
    { to: '/admin', label: 'Admin', icon: Settings },
  ] },
];

const isCurrent = (path, to) => !!to && !!path && (path === to || path.startsWith(`${to}/`));

/**
 * §2.5 Sidebar: expanded (216px, icon + label) or rail (56px, icon + tooltip). AI production uses the clapperboard
 * (§2.7 bans the AI glyph).
 *   mode / onModeChange  controlled mode ('expanded' | 'rail'); omit for an uncontrolled sidebar starting at defaultMode
 *   lockRail             Workbench: rail always, no expand control (§3.2)
 *   drawer               { open, onClose } — at ≤ 768px the sidebar becomes an off-canvas drawer
 *   theme                { value, onToggle } — omit on Workbench (§3.2)
 *   account              { name, initials, role, onOpen }; onSignOut adds the sign-out control
 *   onSearch / onNotifications / onHelp render their items only when given (no dead controls)
 *   nav items: { to, label, icon, href?, current? }: href renders a link (HD-8); current overrides the path match
 */
export default function Sidebar({
  nav = STAFF_NAV, currentPath, onNavigate, mode: controlled, onModeChange, defaultMode = 'expanded', lockRail = false,
  drawer, account, onSignOut, unread, theme, onSearch, onNotifications, onHelp, label = 'Primary',
}) {
  const [own, setOwn] = useState(defaultMode);
  const mode = lockRail ? 'rail' : (controlled || own);
  const setMode = m => { if (onModeChange) onModeChange(m); else setOwn(m); };
  const rail = mode === 'rail';
  const open = !!drawer?.open;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') drawer.onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, drawer]);
  const go = to => { onNavigate?.(to); drawer?.onClose?.(); };
  // An item with href renders as a link (open in a new tab works; a plain click still navigates in the app). current:
  // the caller decides whether the item is the current destination (an item whose `to` carries a query string).
  const item = (it, extra) => {
    const inner = <><Icon icon={it.icon} /><span className="ui-sb__label">{it.label}</span>{extra}<span className="ui-sb__tip" aria-hidden="true">{it.label}</span></>;
    const current = (it.current ?? isCurrent(currentPath, it.to)) ? 'page' : undefined;
    if (it.href) {
      return (
        <a key={it.to || it.label} href={it.href} className="ui-sb__item" aria-current={current} aria-label={rail ? it.label : undefined}
          onClick={e => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); go(it.to); }}>
          {inner}
        </a>
      );
    }
    return (
      <button key={it.to || it.label} type="button" className="ui-sb__item" aria-current={current}
        aria-label={rail ? it.label : undefined} onClick={e => (it.onClick ? it.onClick(e) : go(it.to))}>
        {inner}
      </button>
    );
  };
  return (
    <>
      {open && <div className="ui ui-sb-scrim" onMouseDown={drawer.onClose} aria-hidden="true" />}
      <nav className={cx('ui ui-sb', rail && 'ui-sb--rail', drawer && 'ui-sb--drawer', open && 'ui-sb--open')} aria-label={label}>
        <div className="ui-sb__top">
          <Mark wordmark={!rail} />
          {open
            ? <button type="button" className="ui-btn ui-btn--quiet ui-btn--icon" aria-label="Close navigation" title="Close navigation" onClick={drawer.onClose}><Icon icon={X} /></button>
            : !lockRail && (
              <button type="button" className="ui-btn ui-btn--quiet ui-btn--icon ui-sb__mode" aria-expanded={!rail} aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
                title={rail ? 'Expand sidebar' : 'Collapse sidebar'} onClick={() => setMode(rail ? 'expanded' : 'rail')}>
                <Icon icon={rail ? PanelLeftOpen : PanelLeftClose} />
              </button>
            )}
        </div>
        {onSearch && (
          <button type="button" className="ui-sb__search" onClick={onSearch} aria-label="Search or jump to (Ctrl K)">
            <Icon icon={Search} /><span className="ui-sb__search-label">Search or jump to</span><Kbd>Ctrl K</Kbd>
          </button>
        )}
        <div className="ui-sb__nav">
          {nav.map(g => (
            <div key={g.group} role="group" aria-label={g.group}>
              <p className="ui-sb__group">{g.group}</p>
              {g.items.map(it => item(it))}
            </div>
          ))}
        </div>
        <div className="ui-sb__foot">
          {onNotifications && item({ label: 'Notifications', icon: Bell, onClick: onNotifications }, unread ? <span className="ui-sb__count">{unread > 99 ? '99+' : unread}</span> : null)}
          {onHelp && item({ label: 'Help', icon: CircleHelp, onClick: () => { onHelp(); drawer?.onClose?.(); } })}
          {theme && item({ label: theme.value === 'dark' ? 'Light theme' : 'Dark theme', icon: theme.value === 'dark' ? Sun : Moon, onClick: theme.onToggle })}
          {account && (
            <div className="ui-sb__acct">
              <button type="button" className="ui-sb__acct-btn" onClick={() => { account.onOpen?.(); drawer?.onClose?.(); }} disabled={!account.onOpen}
                aria-label={`Account settings for ${account.name}`} title="Account settings">
                <Avatar name={account.name} initials={account.initials} />
                <span className="ui-sb__who"><b>{account.name}</b><span className="ui-meta">{account.role}</span></span>
              </button>
              {onSignOut && <button type="button" className="ui-btn ui-btn--quiet ui-btn--icon ui-sb__out" aria-label="Sign out" title="Sign out" onClick={onSignOut}><Icon icon={LogOut} /></button>}
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
